import React, { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import SoldOutMessage from '../../Support/SoldOutMessage'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import { connect } from 'react-redux'
import useBedbankRates from 'hooks/useBedbankRates'
import HighlightMessage from 'components/Common/HighlightMessage/HighlightMessage'
import useImpressionHandler from 'hooks/useImpressionHandler'
import { getPlural } from 'lib/string/pluralize'
import BedbankOfferTilePriceDetails from 'components/OfferList/OfferListTiles/Support/BedbankOfferTilePriceDetails'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import TileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import { useBedbankSearchTilePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import BedbankOfferTileSnippetExpanded from '../../Support/BedbankOfferTileSnippetExpanded'
import TextButton from 'components/Luxkit/Button/TextButton'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { queryKeySelectedOfferIds } from 'constants/url'
import { encodeOfferIds } from 'components/Pages/HotelSearchPage/HotelSearchMapPage'
import { getShowOnMapOfferExperimentVariant } from 'selectors/searchSelectors'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import { DAYS_TO_BOOK } from 'constants/bedbank'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import BedbankUpsellPromotion from '../../Support/BedbankUpsellPromotion'
import moment from 'moment'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import { Location } from 'history'
import Group from 'components/utils/Group'
import OfferTilePropertyHeading from '../../SearchOfferTiles/OfferTilePropertyHeading'
import OfferTileLocationSection from '../../SearchOfferTiles/OfferTileLocationSection'
import GeoContext from 'contexts/geoContext'
import { isBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import { Main, MainImageSegment, DetailSegment, GalleryButton } from './SearchTileStyles'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankTileFlightPrice from '../../Support/BedbankTileFlightPrice'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'

const Root = styled.div`
  position: relative;
`

const LeftDetailSegment = styled.div`
  align-self: flex-start;

  > * + * {
    margin-top: ${rem(4)}
  }
`

const StyledGroup = styled(Group)`
  > * + * {
    margin-left: ${rem(8)};
  }
`

const RightDetailSegment = styled.div`
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-width: ${rem(180)};
  align-items: flex-start;
  > * + * {
    align-items: flex-start;
    margin-top: ${rem(8)};
  }
  ${mediaQueryUp.desktop} {
    margin-left: ${rem(20)};
  }
`

const FooterButtons = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${props => props.theme.fontSize.sm};
  width: 100%;
  margin-top: ${rem(16)};
`

function getFilterHighlightMessage(offer: App.BedbankOffer | App.BedbankOfferSummary, hasRate: boolean, hasDates: boolean, filters?: App.OfferListFilters) {
  if (filters?.propertyId && !filters?.searchNearby) {
    // This is a property search result

    if (hasDates) {
      if (!hasRate) {
        return `There are no ${getPlural(offer.saleUnit)} available for your selected dates.`
      }
      return 'We found a great price for your hotel. Book now to secure this rate!'
    }
    else {
      return 'We found the property you were interested in. Select your dates to book now.'
    }
  }
}

interface MappedStateProps {
  location: Location;
  shouldShowMapButton: boolean;
  isBedbankFlightBookingEnabled: boolean;
}

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  filters?: App.OfferListFilters;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
}

const imageParams: ImageParams = {
  tabletAspectRatio: '16:9',
  tabletWidth: '100vw',
  desktopAspectRatio: '16:9',
  desktopWidth: '67vw',
  largeDesktopAspectRatio: '16:9',
  largeDesktopWidth: '762px',
  quality: 'good',
}

function NewBedbankOfferTileExpanded(props: Props & MappedStateProps) {
  const {
    offer,
    filters,
    eagerLoadFirstImage,
    offerUrl,
    location,
    shouldShowMapButton,
    isBedbankFlightBookingEnabled,
  } = props

  const {
    id,
    property,
    tagline,
  } = offer
  const { showTaxExclusivePrices } = useContext(GeoContext)

  const [hotelRates, fetchingRates] = useBedbankRates(id, filters?.rooms, filters?.checkIn, filters?.checkOut)

  const rate = hotelRates[0]
  const promotions = useBedbankSearchTilePromotions(offer.promotions, rate, filters?.checkIn, filters?.checkOut)
  const promotion = promotions.length > 0 ? promotions[0] : null

  const propertyFees = rate?.totals?.propertyFees ?? 0
  const hotelPrice = rate ? showTaxExclusivePrices ? rate.totals.exclusive : rate.totals.inclusive : offer.sell?.price

  const hotelNights = rate?.nights ?? offer?.sell?.los

  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  const addressArray = []
  const { city, countryName } = property.address
  if (city) addressArray.push(city)
  if (countryName) addressArray.push(countryName)

  const locationTitle = property.locationTitleOverride ? property.locationTitleOverride : addressArray.join(', ')

  const { onBedbankSelectDatesPrompt, searchTargetLabel, offerDistanceFromSearchTarget } = useContext(GlobalSearchStateContext)
  const searchTargetDistance = offerDistanceFromSearchTarget?.[offer.id]
  const showSearchDistance = searchTargetLabel && typeof searchTargetDistance === 'number' && searchTargetDistance > 0

  const impressionRef = useImpressionHandler(id)
  const hasDates = !!(filters?.checkIn && filters?.checkOut)
  const highlightMessage = getFilterHighlightMessage(offer, !!rate, hasDates, filters)

  const onSelectDatesPrompt = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onBedbankSelectDatesPrompt?.(offer.id, offer.type, location.search)
  }
  const flightsEnabled = isBedbankFlightBookingEnabled && offer.flights?.airportCode && offer.flights?.flightsEnabled

  const mapSearchUrl = `/search/map?${setSearchParamValue(location.search, queryKeySelectedOfferIds, encodeOfferIds([offer]))}`
  const bedBankOfferPromotionEndDate = offer.promotions[0]?.periodSchedule.to
  const priceNotDiscernible = !hasDates && !offer.sell
  const canUpsell = (hasDates && !promotion && // has dates and not already promoted
    offer.promotions?.length > 0 && // has promotions to upsell
    (offer as App.BedbankOffer).packages?.find(pkg => offer.promotions?.[0]?.id === pkg.promotionId && pkg.sell?.price) && // has package for promo with discounts
    offer.promotions.at(0).los > moment(filters?.checkOut).diff(filters?.checkIn, 'days')) // promo is for longer los than current duration
  const isSaveButtonExperimentEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.tripSearchPageSaveButton)

  return (
    <Root ref={impressionRef}>
      {highlightMessage && <HighlightMessage message={highlightMessage} />}
      <Main>
        <MainImageSegment>
          <ImageCarousel
            images={offer.images}
            imageParams={imageParams}
            eagerLoadFirstImage={eagerLoadFirstImage}
            topRight={
              isSaveButtonExperimentEnabled && (<TripGuard>
                <GalleryButton>
                  <BookmarkButton offer={offer} iconOnly/>
                </GalleryButton>
              </TripGuard>
              )
            }
   />
        </MainImageSegment>
        <DetailSegment>
          {!isSaveButtonExperimentEnabled && (<TripGuard>
            <CSSBreakpoint max="mobile">
              <OffsetBookmarkContainer>
                <BookmarkButton offer={offer} />
              </OffsetBookmarkContainer>
            </CSSBreakpoint>
          </TripGuard>
          )}
          <LeftDetailSegment>
            <VerticalSpacer gap={4}>
              {showUserReviewsRating(property.rating, property.reviewsTotal) && (
                <NumberRating
                  variant="medium"
                  rating={property.rating ?? 0}
                  total={property.reviewsTotal}
                  ratingSubtitle={property.reviewsSource === 'google' ? 'From online reviews' : undefined}
                />
              )}
              <OfferTileLocationSection
                location={locationTitle}
                showSearchDistance={showSearchDistance}
                shouldShowMapLink={shouldShowMapButton}
                mapSearchUrl={mapSearchUrl}
                searchTargetDistance={searchTargetDistance}
                searchTargetLabel={searchTargetLabel}
              />
              {!hasDates && !isSaveButtonExperimentEnabled && (
                <TripGuard>
                  <CSSBreakpoint min="tablet">
                    <FloatingBookmarkContainer>
                      <BookmarkButton offer={offer} />
                    </FloatingBookmarkContainer>
                  </CSSBreakpoint>
                </TripGuard>
              )}
            </VerticalSpacer>
            {promotion && <StyledGroup direction="horizontal" verticalAlign="center" horizontalAlign="start">
              {bedBankOfferPromotionEndDate && <OfferUrgencyCountdownLabel endDate={bedBankOfferPromotionEndDate} daysToBook={DAYS_TO_BOOK}/>}
            </StyledGroup>}
            <div>
              <OfferTilePropertyHeading name={property.name} descriptiveName={tagline} />
            </div>
            {promotion && <div>
              <ProductTypeLabel kind="plain" productType="limited_time_special" />
            </div>}
            {rate && (
              <BedbankCancellationPolicy rate={rate} offer={offer} showNonCancellation/>
            )}
            <TileInclusions
              offerTitle={offer.property?.name ? offer.property.name : offer.name}
              offerURL={offerUrl}
              heading=""
              content={promotion?.rateInclusionsLong}
              tileInclusionsList={offer.inclusions}
              maxInclusionsCount={3}
            />
            {priceNotDiscernible && <BedbankOfferTileSnippetExpanded offer={offer as App.BedbankOffer} />}
          </LeftDetailSegment>
          {!canUpsell && <RightDetailSegment>
            {(rate || offer.sell) &&
              <BedbankOfferTilePriceDetails
                rate={rate}
                rooms={filters.rooms}
                offer={offer}
                filters={filters}
                offerUrl={offerUrl}
                promotions={promotions}
              />}
            {(!rate && hasDates && !fetchingRates) && <div><SoldOutMessage offer={offer} filters={filters} /></div>}
            {priceNotDiscernible && <FooterButtons>
              <TextButton kind="primary" onClick={onSelectDatesPrompt}>Select your dates</TextButton>
              <TextButton kind="tertiary">View details</TextButton>
            </FooterButtons>}
            {!priceNotDiscernible && <FooterButtons>
              <TextButton fit="full-width" kind="primary">View Offer</TextButton>
            </FooterButtons>}
            {flightsEnabled && <BedbankTileFlightPrice offer={offer} filters={filters} totalPrice={totalPrice} hotelNights={hotelNights} />}
          </RightDetailSegment>}
          {canUpsell && <BedbankUpsellPromotion
            offer={offer as App.BedbankOffer}
            rate={rate}
            rooms={filters.rooms}
            filters={filters}
            offerUrl={offerUrl} />}
        </DetailSegment>
      </Main>
    </Root>
  )
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  location: state.router.location,
  shouldShowMapButton: getShowOnMapOfferExperimentVariant(state),
  isBedbankFlightBookingEnabled: isBedbankFlightBookingEnabled(state),
})

export default connect(mapStateToProps)(NewBedbankOfferTileExpanded)
